import { ChatRoomMessageChannel } from '@commenda/prisma';
import {
  BusinessEntity,
  ChatRoomMessageType,
  ChatRoomType,
  ExternalMessageSystem,
  Frequency,
  LegacyDocumentType,
} from '@prisma/client';
import { FileBasic, IFile } from '../file/types';

export interface IChannelReadState {
  channel: ChatRoomMessageChannel;
  lastReadMessageId: string | null;
  unreadMessagesCount: number;
  markedUnreadAt: string | null;
}

export interface IChatRooms {
  chatRooms: {
    id: string;
    type: ChatRoomType;
    /** Null for a thread with no corporation behind it, which is every GENERAL_THREAD. */
    businessEntity: { id: number; name: string | null } | null;
    /** The client company the room belongs to. Additive: a backend without the field leaves it undefined. */
    company?: { id: number; name: string | null } | null;
    chatRoomName: string;
    unreadMessagesCount: number;
    internalNotesCount: number;
    internalNotesUnreadCount: number;
    isClosed: boolean;
    messagesCount: number;
    lastReadMessageId?: string | null;
    markedUnreadAt?: string | null;
    lastMessagePreview?: string | null;
    lastMessageAt?: string | null;
    isPinnedByUser?: boolean;
    pinnedAt?: string | null;
    channels?: IChannelReadState[];
    serviceRequest?: {
      id: string;
      isCommendaFiling: boolean;
      serviceCompositeId?: string | null;
      corporationId?: number | null;
      salesTaxFilingId?: string | null;
      salesTaxRegistrationId?: string | null;
      workflowExecutionId?: string | null;
    };
  }[];
}

export enum UserTypes {
  ACCOUNTANT = 'ACCOUNTANT',
  CLIENT = 'CLIENT',
  ADMIN = 'ADMIN',
  AGENT = 'AGENT',
}

export interface IChatRoomUser {
  id: string;
  name: string;
  avatar: string;
  email: string;
  isAccountant: boolean;
  isAgent?: boolean;
  firmName?: string | null;
}

export interface IChatRoomDocumentRequest {
  documentType: LegacyDocumentType;
  title?: string;
  isTimeSensitive: boolean;
  frequency?: Frequency;
  fiscalYear?: number;
  quarters?: number[];
  months?: number[];
}

export enum ChatRoomAgentMessageKind {
  REPLY = 'REPLY',
  MUTED_NOTICE = 'MUTED_NOTICE',
  RESUMED_NOTICE = 'RESUMED_NOTICE',
  INTRO = 'INTRO',
}

export enum ChatRoomAgentFeedbackRating {
  UP = 'UP',
  DOWN = 'DOWN',
}

export interface IChatRoomAgentRun {
  id: string;
  myRating: ChatRoomAgentFeedbackRating | null;
  canRate: boolean;
}

export interface IChatRoomAgentState {
  canMentionAgent: boolean;
  isMuted: boolean;
  isListening: boolean;
  pendingTriggerMessageIds: string[];
}

export interface IChatRoomMessage {
  id: string;
  type: ChatRoomMessageType;
  message: string;
  isPinned: boolean;
  pinnedByUserId: string | null;
  replyToMessageId?: string;
  sentAt: string; // date time string
  user: IChatRoomUser | null;
  documentRequest?: IChatRoomDocumentRequest;
  files: IFile[];
  isRead?: boolean; // Whether the message has been read
  externalUser: { name: string; email: string };
  externalMessageSource?: ExternalMessageSystem;
  externalMessageId?: string;
  isInternalNoteMessage?: boolean;
  channel?: ChatRoomMessageChannel | null;
  rawMessage?: string;
  sentViaClientName?: string | null;
  agentMessageKind?: ChatRoomAgentMessageKind | null;
  agentRun?: IChatRoomAgentRun | null;
}

export interface IChatRoom {
  id: string;
  type: ChatRoomType;
  displayName: string;
  unreadMessages: IChatRoomMessage[];
  chatMessages: IChatRoomMessage[];
  corporation: BusinessEntity | null;
  lastReadMessageId: string | null;
  unreadMessagesCount: number;
  markedUnreadAt: string | null;
  channels?: IChannelReadState[];
  // BFF pattern:
  // We want to know all the unique users who have participated in the chatRoom
  // this is different from total number of users who have access to this chat.
  participatedUsers: IChatRoomUser[];
  complianceId?: string;
  serviceRequestId?: string;
  marketplaceServiceId?: string;
  companyId?: number;
  serviceRequest?: {
    id: string;
    isCommendaFiling: boolean;
    assignedAgentFirmId?: string | null;
  };
  createdAt: string; // date time string
  lastUpdatedAt: string; // date time string
  isClosed: boolean;
  agentState?: IChatRoomAgentState;
}

export interface ICorporationUnreadChatRoomMessage {
  corporationId?: number;
  corporationName: string;
  chatRoomId: string;
  chatRoomType: ChatRoomType;
  message: {
    id: string;
    message: string;
    sentAt: string;
    messageType: string;
    sender: {
      id: string;
      name: string;
      email: string;
    };
  };
  complianceType?: string;
  complianceId?: string;
  serviceRequestId?: string;
  title: string;
}

export interface ChatProps {
  userId: string;
  onSend: (args: {
    messageType: ChatRoomMessageType;
    message: string;
    files: FileBasic[];
  }) => Promise<boolean>;

  onUpdate: (args: {
    files: FileBasic[];
    messageId: string;
  }) => Promise<unknown>;

  chatRoomId?: string;
  chatRoom?: IChatRoom;

  onFileUpload: (file: File) => Promise<{ fileUrl: string }>;
  onFileDownload: (fileId: number) => Promise<{ signedUrl: string }>;

  downloadMessageZipFile: (messageId: string) => Promise<{ signedUrl: string }>;

  formationDate: string;

  updateAsRead: (chatRoomId: string) => Promise<void>;

  setNotification: (args: {
    show: boolean;
    valid: boolean;
    message: string;
    title: string;
  }) => unknown;
}

export type NativeFile = {
  nativeFile: File;
  localId: string; // This is to uniquely perform operations on this file
  isUploading: boolean;
  fileUrl: string;
};

export interface IUnreadChatRoomMessage {
  // This will be set for threads linked to a corporation.
  // For service request threads, it won't be set if the service request isn't linked to a corporation.
  corporationName?: string;
  filingName: string;
  activityType: string;
  createdBy: string;
  createdAt: string;
  messageId: string;
  corporationId?: number;
  complianceId?: string;
  serviceRequestId?: string;
  chatRoomId: string;
  chatRoomType: ChatRoomType;
}
